/*
 * @Author: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @Date: 2025-04-23 12:01:29
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2026-03-12 16:32:07
 */

/**
 * ActionBar 组件
 *
 * 功能：提供照片查看器的操作按钮工具栏
 * 包含：
 * - 全屏/退出全屏切换
 * - 旋转功能（仅照片）
 * - 缩放控制
 * - 信息面板切换
 * - 去水印功能
 * - AI识图功能
 * - 删除/下载功能
 * - 照片变清晰功能
 * - 评论功能入口
 */
import React from 'react'
import { Tooltip, Divider } from 'antd'
import classnames from 'classnames'
import AiImageRegModal from '../../AiImageRegModal'
import PhotoComments from '../../PhotoComments'
import ai_icon from '../../../../images/ai-chat/ai_icon.png'
import { Photo } from '../types'
import {
  createFromIconfontCN,
  DeleteOutlined,
  DownloadOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  ZoomInOutlined,
  ZoomOutOutlined
} from '@ant-design/icons'

const IconFont = createFromIconfontCN({
  scriptUrl: '//at.alicdn.com/t/font_2193463_rlxcl18f1s.js'
})

interface ActionBarProps {
  photo: Photo
  scale: number
  isFullScreen: boolean
  aiProcessing: boolean
  isComparisonMode: boolean
  activeIndex: number
  canDelete: boolean
  canRectify: boolean
  canRemoveWatermark: boolean
  showHdButton: boolean
  isPhotoClarity: boolean
  aiVisualSearchAB: boolean
  saveLoading: boolean
  isHideInfo: boolean
  isShowComments: boolean
  comments: any[]
  onFullScreen: () => void
  onExitFullScreen: () => void
  onRotate: () => void
  onZoomOut: () => void
  onZoomIn: () => void
  onInfo: () => void
  onRemoveWatermark: () => void
  onCreateRectifyTask: () => void
  onReportSA: (action: string) => void
  onDelete: () => void
  onSave: () => void
  onHdDisplay: () => void
  onSaveComment: (value: string) => void
  onRemoveEventListener: () => void
  onAddEventListener: () => void
  canAiSecurity?: boolean
  onAiSecurityRecognition?: () => void
}

const ActionBar: React.FC<ActionBarProps> = ({
  photo,
  scale,
  isFullScreen,
  aiProcessing,
  isComparisonMode,
  activeIndex,
  canDelete,
  canRectify,
  canAiSecurity = true,
  canRemoveWatermark,
  showHdButton,
  isPhotoClarity,
  aiVisualSearchAB,
  saveLoading,
  isHideInfo,
  isShowComments,
  comments,
  onFullScreen,
  onExitFullScreen,
  onRotate,
  onZoomOut,
  onZoomIn,
  onInfo,
  onRemoveWatermark,
  onCreateRectifyTask,
  onReportSA,
  onDelete,
  onSave,
  onHdDisplay,
  onSaveComment,
  onRemoveEventListener,
  onAddEventListener,
  onAiSecurityRecognition
}) => {
  return (
    <div
      className="actions"
      style={{
        display: aiProcessing ? 'none' : 'flex',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        backdropFilter: 'blur(20px)',
        borderRadius: '8px'
      }}
    >
      {!isFullScreen && (
        <Tooltip title="全屏">
          <span className="action-item" onClick={onFullScreen}>
            <FullscreenOutlined />
          </span>
        </Tooltip>
      )}
      {isFullScreen && (
        <Tooltip title="退出全屏">
          <span className="action-item" onClick={onExitFullScreen}>
            <FullscreenExitOutlined />
          </span>
        </Tooltip>
      )}
      {photo?.mediaType === 0 && (
        <Tooltip title="旋转">
          <span className="action-item" onClick={onRotate}>
            <IconFont type="icon-rotate-right" />
          </span>
        </Tooltip>
      )}
      {photo?.mediaType === 0 && (
        <>
          <Tooltip title="缩小">
            <span
              className={classnames('action-item', {
                disabled: scale === 80
              })}
              onClick={onZoomOut}
            >
              <ZoomOutOutlined />
            </span>
          </Tooltip>
          <Tooltip title="放大">
            <span
              className={classnames('action-item', {
                disabled: scale === 200
              })}
              onClick={onZoomIn}
            >
              <ZoomInOutlined
                style={scale === 200 ? { color: '#646464' } : {}}
              />
            </span>
          </Tooltip>
        </>
      )}

      {/* 只在对比模式下的清晰图不显示分割线和其他按钮 */}
      {(!isComparisonMode || (isComparisonMode && activeIndex === 0)) && (
        <Tooltip title="信息">
          <span className={classnames('action-item')} onClick={onInfo}>
            <InfoCircleOutlined />
          </span>
        </Tooltip>
      )}

      {/* 只在对比模式下的清晰图不显示分割线和其他按钮 */}
      {(!isComparisonMode || (isComparisonMode && activeIndex === 0)) && (
        <>
          <Divider
            orientation="vertical"
            style={{
              height: 20,
              opacity: 0.5
            }}
          />

          {canRemoveWatermark && (
            <Tooltip title="去水印">
              <span
                className="action-item action-item-text"
                onClick={onRemoveWatermark}
                style={{
                  border: '1px solid #fff',
                  borderRadius: '4px',
                  color: '#fff',
                  fontSize: '12px',
                  padding: '2px 6px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '24px',
                  width: 'auto'
                }}
              >
                去水印
              </span>
            </Tooltip>
          )}
          {canAiSecurity && (
            <Tooltip title="隐患识别">
              <span
                className="action-item action-item-text"
                onClick={() => onAiSecurityRecognition?.()}
                style={{
                  border: '1px solid #fff',
                  borderRadius: '4px',
                  color: '#fff',
                  fontSize: '12px',
                  padding: '2px 6px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '24px',
                  width: 'auto'
                }}
              >
                隐患识别
              </span>
            </Tooltip>
          )}
          {canRectify && (
            <Tooltip title="发起整改">
              <span
                className="action-item action-item-text"
                onClick={onCreateRectifyTask}
                style={{
                  border: '1px solid #fff',
                  borderRadius: '4px',
                  color: '#fff',
                  fontSize: '12px',
                  padding: '2px 6px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '24px',
                  width: 'auto'
                }}
              >
                提整改
              </span>
            </Tooltip>
          )}
        </>
      )}

      {photo?.mediaType !== 1 && aiVisualSearchAB && (
        <AiImageRegModal
          image={photo?.smallURL || ''}
          fromPlace="photoPreview"
          nodeClassName=""
          clickNode={
            <Tooltip title="AI识图">
              <span
                className="action-item"
                onClick={() => onReportSA('photoPreview')}
              >
                <img src={ai_icon} className="action-icon" alt="" />
              </span>
            </Tooltip>
          }
        />
      )}

      {(!isComparisonMode || (isComparisonMode && activeIndex === 0)) &&
        canDelete && (
          <Tooltip title="删除">
            <span className="action-item" onClick={onDelete}>
              <DeleteOutlined />
            </span>
          </Tooltip>
        )}
      {/* 下载按钮在所有模式下都显示 */}
      <Tooltip title="下载">
        <span
          className="action-item"
          onClick={saveLoading ? undefined : onSave}
        >
          {saveLoading ? <LoadingOutlined /> : <DownloadOutlined />}
        </span>
      </Tooltip>

      {/*只有全部照片页面才能看评论；信息模式下和对比模式下的清晰图隐藏预览地方的评论组件*/}
      {isHideInfo && isShowComments && !isComparisonMode && (
        <div style={{ position: 'relative' }}>
          <div
            onFocus={onRemoveEventListener}
            onBlur={onAddEventListener}
            className="photoCommentsWarp"
          >
            <PhotoComments
              currentPhoto={photo}
              location="preview"
              themeColor="photoComments"
              handleSaveComment={onSaveComment}
              handleReportSA={onReportSA}
              comments={comments || []}
            />
          </div>
        </div>
      )}
      {/* 变清晰按钮只在非对比模式下显示 */}
      {photo?.mediaType === 0 &&
        showHdButton &&
        canRemoveWatermark &&
        !aiProcessing &&
        !isComparisonMode &&
        isPhotoClarity && (
          <span
            className="action-item"
            style={{
              background: 'linear-gradient(to right, #4264FF, #62AAFF)',
              width: '181px',
              height: '40px',
              color: 'white',
              fontSize: '17px',
              position: 'absolute',
              bottom: '50px',
              left: '20vw',
              padding: '2px 8px',
              borderRadius: '40px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={onHdDisplay}
          >
            &nbsp;&nbsp;照片变清晰（限免）
          </span>
        )}
    </div>
  )
}

export default ActionBar
